'use client';

import { useState, useEffect } from 'react';
import type { EntityDocumentation, DocType } from '@/lib/command-center/types';

interface DocumentationPanelProps {
  entityId: string;
  entityName?: string;
  workspaceId: string;
  onDocChange?: () => void;
  compact?: boolean;
}

const DOC_TYPE_LABELS: Record<DocType, { label: string; color: string; icon: string }> = {
  runbook: { label: 'Runbook', color: 'text-red-400', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' },
  adr: { label: 'ADR', color: 'text-purple-400', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  readme: { label: 'README', color: 'text-blue-400', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  wiki: { label: 'Wiki', color: 'text-green-400', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' },
  confluence: { label: 'Confluence', color: 'text-blue-300', icon: 'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z' },
  notion: { label: 'Notion', color: 'text-foreground', icon: 'M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z' },
  diagram: { label: 'Diagram', color: 'text-yellow-400', icon: 'M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z' },
  api_spec: { label: 'API Spec', color: 'text-accent', icon: 'M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
  other: { label: 'Other', color: 'text-gray-400', icon: 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z' },
};

export function DocumentationPanel({
  entityId,
  entityName,
  workspaceId,
  onDocChange,
  compact = false,
}: DocumentationPanelProps) {
  const [docs, setDocs] = useState<EntityDocumentation[]>([]);
  const [health, setHealth] = useState<{
    total_docs: number;
    verified_docs: number;
    stale_docs: number;
    primary_docs: number;
  }>({ total_docs: 0, verified_docs: 0, stale_docs: 0, primary_docs: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [adding, setAdding] = useState(false);

  // Form state
  const [docType, setDocType] = useState<DocType>('readme');
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);

  // Fetch docs
  const fetchDocs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/secure/command-center/entities/${entityId}/docs`);
      if (!response.ok) {
        throw new Error('Failed to fetch documentation');
      }
      const data = await response.json();
      setDocs(data.docs || []);
      setHealth(data.health || { total_docs: 0, verified_docs: 0, stale_docs: 0, primary_docs: 0 });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch documentation');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  // Add doc
  const handleAddDoc = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !url.trim()) return;

    try {
      setAdding(true);
      const response = await fetch(`/api/secure/command-center/entities/${entityId}/docs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          doc_type: docType,
          title: title.trim(),
          url: url.trim(),
          description: description.trim() || undefined,
          is_primary: isPrimary,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to add documentation');
      }

      // Reset form and refresh
      setDocType('readme');
      setTitle('');
      setUrl('');
      setDescription('');
      setIsPrimary(false);
      setShowAddForm(false);
      await fetchDocs();
      onDocChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add documentation');
    } finally {
      setAdding(false);
    }
  };

  // Verify doc
  const handleVerifyDoc = async (docId: string) => {
    try {
      const response = await fetch(
        `/api/secure/command-center/entities/${entityId}/docs?doc_id=${docId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to verify documentation');
      }

      await fetchDocs();
      onDocChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify documentation');
    }
  };

  // Remove doc
  const handleRemoveDoc = async (docId: string) => {
    if (!confirm('Remove this documentation link?')) return;

    try {
      const response = await fetch(
        `/api/secure/command-center/entities/${entityId}/docs?doc_id=${docId}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        throw new Error('Failed to remove documentation');
      }

      await fetchDocs();
      onDocChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove documentation');
    }
  };

  // Check if doc is stale
  const isStale = (doc: EntityDocumentation) => {
    if (!doc.last_known_update) return false;
    const staleAfter = doc.stale_after_days || 90;
    const lastUpdate = new Date(doc.last_known_update);
    const staleDate = new Date();
    staleDate.setDate(staleDate.getDate() - staleAfter);
    return lastUpdate < staleDate;
  };

  if (compact) {
    const healthScore =
      health.total_docs > 0
        ? Math.round((health.verified_docs / health.total_docs) * 100)
        : 0;
    return (
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-foreground-tertiary">Docs:</span>
          <span className="text-foreground font-medium">{health.total_docs}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-foreground-tertiary">Health:</span>
          <span
            className={`font-medium ${
              healthScore >= 80
                ? 'text-green-400'
                : healthScore >= 50
                  ? 'text-yellow-400'
                  : 'text-red-400'
            }`}
          >
            {healthScore}%
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface/50 rounded-xl border border-border-muted overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-muted">
        <div>
          <h3 className="text-sm font-medium text-foreground">Documentation</h3>
          {entityName && <p className="text-xs text-foreground-tertiary">for {entityName}</p>}
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-3 py-1.5 text-sm text-accent hover:text-accent hover:bg-accent-muted rounded-lg transition-colors"
        >
          {showAddForm ? 'Cancel' : '+ Add Doc'}
        </button>
      </div>

      {/* Health Summary */}
      <div className="grid grid-cols-4 gap-4 p-4 bg-background/30 border-b border-border-muted">
        <div className="text-center">
          <div className="text-xs text-foreground-tertiary mb-1">Total</div>
          <div className="text-lg font-semibold text-foreground">{health.total_docs}</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-foreground-tertiary mb-1">Verified</div>
          <div className="text-lg font-semibold text-green-400">{health.verified_docs}</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-foreground-tertiary mb-1">Stale</div>
          <div className="text-lg font-semibold text-yellow-400">{health.stale_docs}</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-foreground-tertiary mb-1">Primary</div>
          <div className="text-lg font-semibold text-blue-400">{health.primary_docs}</div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mx-4 mt-4 px-3 py-2 text-sm text-red-400 bg-red-900/20 border border-red-500/30 rounded">
          {error}
        </div>
      )}

      {/* Add form */}
      {showAddForm && (
        <form onSubmit={handleAddDoc} className="p-4 border-b border-border-muted bg-background/30">
          <div className="space-y-3">
            {/* Doc Type */}
            <div>
              <label className="block text-xs text-foreground-tertiary mb-1">Documentation Type</label>
              <div className="grid grid-cols-3 gap-2">
                {(Object.keys(DOC_TYPE_LABELS) as DocType[]).slice(0, 6).map((dt) => (
                  <button
                    key={dt}
                    type="button"
                    onClick={() => setDocType(dt)}
                    className={`p-2 text-xs rounded-lg border transition-colors ${
                      docType === dt
                        ? `${DOC_TYPE_LABELS[dt].color} border-current bg-current/10`
                        : 'text-foreground-tertiary border-border-muted hover:border-border/30'
                    }`}
                  >
                    <svg className="w-4 h-4 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={DOC_TYPE_LABELS[dt].icon} />
                    </svg>
                    <div className="truncate">{DOC_TYPE_LABELS[dt].label}</div>
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {(Object.keys(DOC_TYPE_LABELS) as DocType[]).slice(6).map((dt) => (
                  <button
                    key={dt}
                    type="button"
                    onClick={() => setDocType(dt)}
                    className={`p-2 text-xs rounded-lg border transition-colors ${
                      docType === dt
                        ? `${DOC_TYPE_LABELS[dt].color} border-current bg-current/10`
                        : 'text-foreground-tertiary border-border-muted hover:border-border/30'
                    }`}
                  >
                    <svg className="w-4 h-4 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={DOC_TYPE_LABELS[dt].icon} />
                    </svg>
                    <div className="truncate">{DOC_TYPE_LABELS[dt].label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Title and URL */}
            <div>
              <label className="block text-xs text-foreground-tertiary mb-1">
                Title <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Production Runbook"
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground placeholder:text-foreground-muted focus:border-accent focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-xs text-foreground-tertiary mb-1">
                URL <span className="text-red-400">*</span>
              </label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground placeholder:text-foreground-muted focus:border-accent focus:outline-none"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs text-foreground-tertiary mb-1">Description (optional)</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this document..."
                rows={2}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground placeholder:text-foreground-muted focus:border-accent focus:outline-none resize-none"
              />
            </div>

            {/* Primary */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isPrimary}
                onChange={(e) => setIsPrimary(e.target.checked)}
                className="w-4 h-4 rounded border-border bg-background text-accent focus:ring-ring"
              />
              <span className="text-sm text-foreground-secondary">Primary documentation for this type</span>
            </label>

            {/* Submit */}
            <button
              type="submit"
              disabled={adding || !title.trim() || !url.trim()}
              className="w-full px-4 py-2 text-sm font-medium bg-accent text-accent-foreground rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {adding ? 'Adding...' : 'Add Documentation'}
            </button>
          </div>
        </form>
      )}

      {/* Doc list */}
      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center text-foreground-tertiary">
            <div className="inline-block w-5 h-5 border-2 border-border border-t-orange rounded-full animate-spin" />
          </div>
        ) : docs.length === 0 ? (
          <div className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-cream/5 mb-3">
              <svg className="w-6 h-6 text-foreground-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            </div>
            <p className="text-foreground-tertiary text-sm">No documentation linked</p>
            <p className="text-foreground-muted text-xs mt-1">
              Link runbooks, ADRs, wikis, and more
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border-muted">
            {docs.map((doc) => {
              const stale = isStale(doc);
              return (
                <div key={doc.id} className="p-4 hover:bg-cream/5 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`p-2 rounded-lg bg-current/10 ${DOC_TYPE_LABELS[doc.doc_type].color}`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={DOC_TYPE_LABELS[doc.doc_type].icon} />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-foreground hover:text-accent transition-colors"
                          >
                            {doc.title}
                          </a>
                          {doc.is_primary && (
                            <span className="px-1.5 py-0.5 text-xs bg-blue-900/30 text-blue-400 rounded">
                              Primary
                            </span>
                          )}
                          {doc.is_verified && (
                            <span className="px-1.5 py-0.5 text-xs bg-green-900/30 text-green-400 rounded flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Verified
                            </span>
                          )}
                          {stale && (
                            <span className="px-1.5 py-0.5 text-xs bg-yellow-900/30 text-yellow-400 rounded">
                              Stale
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-foreground-tertiary">
                          <span className={DOC_TYPE_LABELS[doc.doc_type].color}>
                            {DOC_TYPE_LABELS[doc.doc_type].label}
                          </span>
                        </div>
                        {doc.description && (
                          <p className="text-xs text-foreground-tertiary mt-1">{doc.description}</p>
                        )}
                        <div className="text-xs text-foreground-muted mt-1">
                          Added {new Date(doc.created_at).toLocaleDateString()}
                          {doc.verified_at && (
                            <> | Verified {new Date(doc.verified_at).toLocaleDateString()}</>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {!doc.is_verified && (
                        <button
                          onClick={() => handleVerifyDoc(doc.id)}
                          className="p-1.5 text-foreground-muted hover:text-green-400 hover:bg-green-900/20 rounded transition-colors"
                          title="Mark as verified"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                      )}
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 text-foreground-muted hover:text-blue-400 hover:bg-blue-900/20 rounded transition-colors"
                        title="Open link"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                      <button
                        onClick={() => handleRemoveDoc(doc.id)}
                        className="p-1.5 text-foreground-muted hover:text-red-400 hover:bg-red-900/20 rounded transition-colors"
                        title="Remove documentation"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default DocumentationPanel;
